import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { FC, useEffect } from 'react';
import {
  getEnumOrganizations,
  setErrors,
  TenderTypes,
  useAPIQuery,
  useCreatePlan,
  usePlanForms,
  usePlanTypes,
  useUpdatePlan,
} from '@web-kc/core/api';
import CloseIcon from '@mui/icons-material/Close';
import { CustomDialog } from '@web-kc/shared/modals';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { ControlledTabs } from '@web-kc/shared/ui';
import { useApp } from '@web-kc/core/providers';

interface Props {
  handleClose: (submitted: boolean) => void;
  defaultValues?: TenderTypes['TenderPlanRequest'];
  planId?: number;
}

const currentYear = new Date().getFullYear();

export const years = [
  { key: currentYear, title: String(currentYear) },
  { key: currentYear + 1, title: String(currentYear + 1) },
];

export const CreateEditPlanModal: FC<Props> = ({
  handleClose,
  defaultValues,
  planId,
}) => {
  const { t } = useTranslation();
  const { data: dataForms, isPending: isLoadingForms } = usePlanForms();
  const { data: dataTypes, isPending: isLoadingTypes } = usePlanTypes();
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useAPIQuery(getEnumOrganizations);

  const {
    mutateAsync: create,
    isPending: isLoading,
    error: errorCreate,
  } = useCreatePlan();
  const { mutateAsync: update, isPending: isLoadingUpdate } = useUpdatePlan();

  const isKc = useApp() === 'kc';
  const { handleSubmit, control } = useForm<TenderTypes['TenderPlanRequest']>({
    defaultValues,
  });

  const onSubmit = (values: TenderTypes['TenderPlanRequest']) => {
    (defaultValues ? update({ id: planId!, ...values }) : create(values)).then(
      () => handleClose(true),
    );
  };

  useEffect(() => {
    if (errorCreate) setErrors(control, errorCreate);
  }, [control, errorCreate]);

  return (
    <CustomDialog
      open
      onClose={() => handleClose(false)}
      sx={{ '.MuiDialog-paper': { maxWidth: '550px' } }}
    >
      <Box p="20px 20px 0 20px">
        <Typography variant="h5">
          {t(`plans:${defaultValues ? 'editPlan' : 'newPlan'}`)}
        </Typography>
      </Box>
      <IconButton
        onClick={() => handleClose(false)}
        sx={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: '20px' }}>
        {isLoadingTypes || isLoadingForms ? (
          <CircularProgress sx={{ display: 'block', marginX: 'auto' }} />
        ) : (
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              display="grid"
              gridTemplateColumns="108px 1fr"
              rowGap="30px"
              columnGap="24px"
              alignItems="center"
            >
              {isKc && (
                <>
                  <Typography>{t('plans:organization')}</Typography>
                  <ControlledTabs
                    name="organization"
                    tabs={organizations}
                    loading={isLoadingOrganizations}
                    control={control}
                    rules={{ required: t('common:validations.required') }}
                    sx={{ overflowX: 'auto' }}
                    tabValueKey="id"
                    tabLabelKey="name"
                    withTranslation
                  />
                </>
              )}
              <Typography>{t('plans:year')}</Typography>
              <ControlledTabs
                name="year"
                tabs={years}
                control={control}
                rules={{ required: t('common:validations.required') }}
                tabValueKey="key"
                tabLabelKey="title"
              />
              <Typography>{t('plans:term')}</Typography>
              <ControlledTabs
                name="term"
                tabs={dataTypes}
                control={control}
                rules={{ required: t('common:validations.required') }}
                tabValueKey="id"
                tabLabelKey="name"
              />
              {isKc && (
                <>
                  <Typography>{t('plans:form')}</Typography>
                  <ControlledTabs
                    name="form"
                    tabs={dataForms}
                    control={control}
                    rules={{ required: t('common:validations.required') }}
                    tabValueKey="id"
                    tabLabelKey="name"
                  />
                </>
              )}
            </Box>

            <Box display="flex" gap="10px" justifyContent="flex-end" mt="40px">
              <Button variant="outlined" onClick={() => handleClose(false)}>
                {t('common:common.cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                color={isKc ? 'primary' : 'secondary'}
                loading={isLoading || isLoadingUpdate}
              >
                {t(`common:common.${defaultValues ? 'save' : 'create'}`)}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </CustomDialog>
  );
};
